import React, { useRef, useEffect, useState } from 'react';
import { Grid } from '@material-ui/core';
import { Message } from 'models/Message';
import { client, consoleLogInDev, track } from 'shared';
import AuthModal from 'parent-portal/login/AuthModal';
import { useHistory } from 'react-router-dom';
import { useUserContext } from 'UserContext';
import { useWidth } from 'reusableComponents/useWidth';
import MobileMessageForm from './MobileMessageForm';
import WebMessageForm from './WebMessageForm';
import PaymentNotSetup from './PaymentNotSetup';
import MessageInput from './MessageInput';
import { IProvider } from './models';
import { UserContextUser } from 'models';
import { Environment, RequirePaymentSetupToChat } from 'environmentVariables';

interface ISitterChatProps {
    providerIdObject: IProvider;
    onMessageSent?: () => void;
    limit?: number;
    providerName?: string;
    completedBackgroundCheck: boolean;
    providerImage?: string;
}

const ProviderMessenger = ({
    providerIdObject,
    onMessageSent,
    limit = 10,
    providerName,
    providerImage,
}: ISitterChatProps) => {
    const messagesEndRef = useRef<null | HTMLDivElement>(null);
    const bottomOfThread = useRef<null | HTMLDivElement>(null);
    const [contactThread, setContactThread] = useState<Message[]>([]);
    const [messageToSend, setMessageToSend] = useState('');
    const [requirePaymentSetupToChat, setRequirePaymentSetupToChat] = useState(false);
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const [showCompleteProfileDialog, setShowCompleteProfileDialog] = useState(false);
    const breakpoint = useWidth();
    const smallScreen = ['xs', 'sm'].includes(breakpoint);
    const mounted = useRef(true);

    const { user, role } = useUserContext();
    const paymentSetup = user?.payment_information;
    const isPaymentSetup = user?.customer_id || (!!paymentSetup && paymentSetup.length > 0);

    const history = useHistory<{ message: string; navToScreen: string; navToScreenText: string }>();

    useEffect(() => {
        if (history.location?.state?.message) {
            setMessageToSend(history.location.state.message);
        }
    }, []);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (RequirePaymentSetupToChat === 'true') {
            setRequirePaymentSetupToChat(true);
        }

        firstThreadLoad()
            .then(() => longPoll())
            .catch(consoleLogInDev);
    }, [providerIdObject?.user?.id]);

    const firstThreadLoad = async () => {
        if (providerIdObject) {
            client(`api/first-thread-load/?one=${providerIdObject.user.id}&limit=20`).then((result) => {
                setContactThread(result);
            });
        }
    };

    const longPoll = async () => {
        if (mounted.current && providerIdObject) {
            client(`api/contact/?one=${providerIdObject.user.id}&limit=20`)
                .then((res) => {
                    if (res) {
                        setContactThread(res);
                        longPoll();
                    } else {
                        longPoll();
                    }
                })
                .catch(() => {
                    planB();
                });
        }
    };

    const planB = async () => {
        while (mounted.current) {
            await firstThreadLoad();
            await new Promise((r) => setTimeout(r, 5000));
        }
    };

    function checkForPhone(e: string) {
        let phonePattern = /\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/;
        const includesPhone = phonePattern.test(e);
        if (!!includesPhone) {
            track('Family sent a message to a provider including a phone number');
        }
    }

    useEffect(() => {
        if (messagesEndRef && messagesEndRef.current) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }

        if (bottomOfThread && bottomOfThread.current) {
            bottomOfThread.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [contactThread]);

    const sendMessage = () => {
        track('Parent Portal Send Message Pressed');
        if (!messageToSend || isSendingMessage) {
            track('Parent Portal Prevent Message Send', { isMessageToSendEmpty: !messageToSend, isSendingMessage });
            return;
        }

        if (providerIdObject) {
            setIsSendingMessage(true);
            const body = {
                sender: user?.id,
                reciever: providerIdObject.user.id,
                message: messageToSend,
                message_type: 'PARENT_PORTAL',
                image: '',
                navigation_screen: history.location?.state?.navToScreen || undefined,
                navigation_button_text: history.location?.state?.navToScreenText || undefined,
            };

            client('api/messages/message-user/', { body })
                .then((response) => {
                    track('Parent Portal Message Sent');
                    onMessageSent?.();
                })
                .catch((e) => {
                    if (Environment !== 'production') {
                        console.log(e);
                    }

                    track('Parent Portal Message Error');
                })
                .finally(() => {
                    setIsSendingMessage(false);
                    setMessageToSend('');
                    firstThreadLoad();
                });
        }
    };

    function ChatContent() {
        const formProps = {
            providerIdObject,
            providerName,
            contactThread,
            bottomOfThread,
            user: user as UserContextUser,
            messageToSend,
            messagesEndRef,
            MessageInput: (
                <MessageInput
                    {...{
                        setMessageToSend,
                        messageToSend,
                        sendMessage,
                        isSendingMessage,
                        messagesEndRef,
                        checkForPhone,
                    }}
                />
            ),
            providerImage,
            role,
        };

        if (requirePaymentSetupToChat && !isPaymentSetup) {
            return <PaymentNotSetup providerName={providerName} />;
        }

        return smallScreen ? <MobileMessageForm {...formProps} /> : <WebMessageForm {...formProps} />;
    }

    return (
        <Grid container>
            {/* DO NOT CALL THIS AS A COMPONENT IT WILL CAUSE A LOSS OF FOCUS IN MSG INPUT*/}
            {ChatContent()}
            <AuthModal
                location="Third Party Signup"
                open={showCompleteProfileDialog}
                onComplete={() => {
                    setShowCompleteProfileDialog(false);
                }}
                onClose={() => {
                    setShowCompleteProfileDialog(false);
                }}
                redirect={false}
                signup
            />
        </Grid>
    );
};

export default ProviderMessenger;
